import React, { useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Lock, Mail, ArrowLeft, CheckCircle } from 'lucide-react';
import api from '../lib/api';

export const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  
  const [step, setStep] = useState(token ? 'reset' : 'request');
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [success, setSuccess] = useState(false);
  const [resetToken, setResetToken] = useState(token || '');

  const handleRequestReset = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await api.post('/auth/request-password-reset', { email });
      toast.success('Verifică-ți email-ul pentru instrucțiuni');
      
      // If token is returned (SMTP not configured), show it
      if (response.data.token) {
        setResetToken(response.data.token);
        setStep('reset');
        toast.info('SMTP nu e configurat. Folosește token-ul pentru resetare.');
      } else {
        setSuccess(true);
      }
    } catch (error) {
      toast.error('Eroare la trimiterea cererii');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      toast.error('Parolele nu coincid');
      return;
    }
    
    if (newPassword.length < 6) {
      toast.error('Parola trebuie să aibă minim 6 caractere');
      return;
    }
    
    setLoading(true);
    try {
      await api.post('/auth/reset-password', {
        token: resetToken,
        new_password: newPassword
      });
      toast.success('Parola a fost resetată cu succes');
      setSuccess(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Eroare la resetarea parolei');
    } finally {
      setLoading(false);
    }
  };

  if (success && step === 'reset') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-8 pb-8 text-center">
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold mb-2">Parolă Resetată!</h2>
            <p className="text-muted-foreground mb-6">
              Parola ta a fost schimbată cu succes.
            </p>
            <Button onClick={() => navigate('/login')} className="w-full">
              Mergi la Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (success && step === 'request') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-8 pb-8 text-center">
            <Mail className="w-16 h-16 text-blue-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold mb-2">Verifică Email-ul</h2>
            <p className="text-muted-foreground mb-6">
              Dacă adresa există în sistem, vei primi un email cu instrucțiuni de resetare.
            </p>
            <Link to="/login">
              <Button variant="outline" className="w-full">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Înapoi la Login
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center pb-2">
          <div className="w-16 h-16 bg-gradient-to-br from-orange-400 to-red-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Lock className="w-8 h-8 text-white" />
          </div>
          <CardTitle className="text-2xl">
            {step === 'request' ? 'Resetare Parolă' : 'Setează Parola Nouă'}
          </CardTitle>
          <p className="text-muted-foreground text-sm mt-2">
            {step === 'request' 
              ? 'Introdu email-ul pentru a primi link-ul de resetare'
              : 'Alege o parolă nouă pentru contul tău'
            }
          </p>
        </CardHeader>
        <CardContent>
          {step === 'request' ? (
            <form onSubmit={handleRequestReset} className="space-y-4">
              <div>
                <Label>Email</Label>
                <Input
                  type="email"
                  placeholder="email@adsem.ro"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
              
              <Button type="submit" disabled={loading} className="w-full">
                {loading ? 'Se trimite...' : 'Trimite Link de Resetare'}
              </Button>
              
              <div className="text-center">
                <Link to="/login" className="text-sm text-muted-foreground hover:text-foreground">
                  <ArrowLeft className="w-4 h-4 inline mr-1" />
                  Înapoi la Login
                </Link>
              </div>
            </form>
          ) : (
            <form onSubmit={handleResetPassword} className="space-y-4">
              {!token && (
                <div>
                  <Label>Token de Resetare</Label>
                  <Input
                    value={resetToken}
                    onChange={(e) => setResetToken(e.target.value)}
                    placeholder="Token primit"
                    required
                  />
                </div>
              )}
              
              <div>
                <Label>Parola Nouă</Label>
                <Input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Minim 6 caractere"
                  required
                />
              </div>
              
              <div>
                <Label>Confirmă Parola</Label>
                <Input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Repetă parola"
                  required
                />
              </div>
              
              <Button type="submit" disabled={loading} className="w-full">
                {loading ? 'Se procesează...' : 'Resetează Parola'}
              </Button>
              
              <div className="text-center">
                <Link to="/login" className="text-sm text-muted-foreground hover:text-foreground">
                  <ArrowLeft className="w-4 h-4 inline mr-1" />
                  Înapoi la Login
                </Link>
              </div>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ResetPassword;
